import BigNumber from 'bignumber.js';
import { Atom, useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Styles, css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import { SparkPointPrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';

import { SparkRewardRankingGraph } from './Graph';

export interface PercentilePoint {
  percentile: number;
  pointAmount: BigNumber;
}

const topStyle = css.raw({
  bgGradient: 'programs.vip',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const accentStyle = css.raw({
  color: 'content.accent.info',
});

export const SparkRewardRanking = ({
  css: cssProp,
  userPointAtom,
  percentilePointsAtom,
}: {
  css?: Styles;
  userPointAtom: Atom<BigNumber | undefined>;
  percentilePointsAtom: Atom<PercentilePoint[] | undefined>;
}) => {
  const percentilePoints = useAtomValue(percentilePointsAtom)?.sort((a, b) => a.percentile - b.percentile);
  const userPoint = useAtomValue(userPointAtom);

  let currentPercentileIndex: number | undefined;
  let currentPercentilePoint: PercentilePoint | undefined;
  let nextPercentilePoint: PercentilePoint | undefined;

  if (userPoint !== undefined && percentilePoints) {
    currentPercentileIndex = percentilePoints.findIndex((x) => userPoint.isGreaterThanOrEqualTo(x.pointAmount));
    if (currentPercentileIndex !== -1) currentPercentilePoint = percentilePoints.at(currentPercentileIndex);
    if (currentPercentileIndex >= 1) nextPercentilePoint = percentilePoints.at(currentPercentileIndex - 1);
    else if (currentPercentileIndex === -1) nextPercentilePoint = percentilePoints.at(-1);
  }

  return (
    <section
      className={css(
        vstack.raw({ p: 16, pb: [20, 40], minHeight: [320, 540], rounded: 12, bg: 'background.default.level-0' }),
        cssProp,
      )}
    >
      <div
        className={css({
          flex: 1,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 4,
          textStyle: 'Title/S',
          color: 'content.default.level-5',
        })}
      >
        {!nextPercentilePoint && currentPercentilePoint ? (
          <div>
            Congrats 🎉
            <div className={css({ display: ['inherit', 'none'], height: 4 })} /> You're in the{' '}
            <span className={css(topStyle)}>
              <FormattedMessage
                id='Autogen_common.r251126.s_top_currentPercentilePoint_percentile'
                values={{
                  currentPercentilePoint_percentile: currentPercentilePoint.percentile,
                }}
              />
            </span>
            <br className={css({ display: ['inherit', 'none'] })} /> of Spark earners
          </div>
        ) : nextPercentilePoint && userPoint ? (
          <div>
            Earn{' '}
            <span className={css(accentStyle)}>
              +
              {numberFormatter(nextPercentilePoint.pointAmount.minus(userPoint).toString(), {
                withComma: true,
                precision: SparkPointPrecision,
              })}
            </span>{' '}
            more Sparks
            <br className={css({ display: ['inherit', 'none'] })} /> to reach{' '}
            <span className={css(accentStyle)}>
              <FormattedMessage
                id='Autogen_common.r251126.s_top_nextPercentilePoint_percentile'
                values={{
                  nextPercentilePoint_percentile: nextPercentilePoint.percentile,
                }}
              />
            </span>
          </div>
        ) : null}
      </div>
      <SparkRewardRankingGraph
        currentPercentileIndex={currentPercentileIndex}
        percentiles={percentilePoints?.map((x) => x.percentile)}
        css={css.raw({ mx: 4, alignSelf: 'center', width: '100%', maxWidth: [300, 640] })}
      />
    </section>
  );
};
